import React from 'react';
import { X, Check } from 'lucide-react';

const ServiceModal = ({ serviceId, closeModal }) => {
  const serviceData = {
    vcto: {
      icon: 'Crown',
      title: 'vCTO & IT Strategy',
      gradient: 'gradient-blue',
      description: 'Our Virtual Chief Technology Officer (vCTO) service provides executive-level technology leadership without the full-time cost. We embed ourselves within your leadership team to ensure technology decisions align with business objectives.',
      offerings: [
        'Strategic Technology Roadmapping: 3-5 year technology plans aligned with business growth',
        'IT Governance & Policy: Establish frameworks for technology decision-making and risk management',
        'Vendor Management: Negotiate contracts and manage technology vendor relationships',
        'Technology Budgeting: Optimize IT spend and forecast future technology investments',
        'Digital Transformation: Guide your organization through technology-driven business change',
        'Board-Level Reporting: Clear communication of technology strategy and metrics to stakeholders',
      ],
      benefits: [
        'Executive-level expertise at fraction of full-time CTO cost',
        'Objective, unbiased technology recommendations',
        'Accelerated digital transformation initiatives',
        'Reduced technology risk and improved compliance',
        'Clear alignment between IT and business goals',
      ],
      perfectFor: 'Growing SMEs preparing for scaling, companies lacking IT leadership, organizations undergoing digital transformation.',
    },
    cybersecurity: {
      icon: 'Shield',
      title: 'Cybersecurity & Compliance',
      gradient: 'bg-gradient-to-r from-logo-darkblue to-logo-teal',
      description: 'Protect your business assets, customer data, and reputation with our comprehensive cybersecurity services. We help you navigate complex compliance requirements while building resilient security infrastructure.',
      offerings: [
        'Security Assessments & Audits: Identify vulnerabilities and risks in your current infrastructure',
        'Compliance Management: SOC2, GDPR, HIPAA, PCI-DSS readiness and ongoing compliance',
        'Penetration Testing: Simulated attacks to identify weaknesses before real threats do',
        'Incident Response Planning: Prepare for and respond to security breaches effectively',
        'Employee Security Training: Build a security-aware culture through regular training',
        '24/7 Threat Monitoring: Continuous surveillance and rapid threat response',
      ],
      frameworks: ['SOC 2 Type II', 'GDPR', 'HIPAA', 'PCI-DSS', 'ISO 27001', 'NIST'],
      perfectFor: 'Financial services, healthcare organizations, e-commerce businesses, any company handling sensitive data.',
    },
    cloud: {
      icon: 'Cloud',
      title: 'Cloud & Infrastructure',
      gradient: 'bg-gradient-to-r from-logo-lightblue to-logo-teal',
      description: 'Modernize your IT infrastructure with scalable, secure cloud solutions. We help you leverage the power of cloud computing to improve agility, reduce costs, and enhance performance.',
      offerings: [
        'Cloud Migration: Seamlessly move workloads to AWS, Azure, or Google Cloud',
        'Cloud Optimization: Reduce costs through rightsizing and reserved instance planning',
        'Hybrid & Multi-Cloud: Design and manage complex cloud environments across providers',
        'Infrastructure as Code: Automate infrastructure deployment with Terraform, CloudFormation',
        'Disaster Recovery: Ensure business continuity with robust backup and recovery solutions',
        'Network Architecture: Design secure, high-performance network topologies',
      ],
      platforms: ['AWS', 'Microsoft Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'VMware'],
      perfectFor: 'Businesses planning cloud migration, companies with growing infrastructure needs, organizations seeking cost optimization.',
    },
    mobile: {
      icon: 'Smartphone',
      title: 'Web & Mobile Applications',
      gradient: 'bg-gradient-to-r from-logo-green to-logo-green3',
      description: 'Transform your business processes and customer engagement with custom web and mobile applications. We build scalable, user-centric solutions that drive efficiency and growth.',
      offerings: [
        'Custom Web Applications: React, Angular, Vue.js, Node.js, Python, PHP applications',
        'Mobile App Development: Native iOS (Swift), Android (Kotlin), and Cross-platform (React Native, Flutter)',
        'UI/UX Design: User-centered design that enhances engagement and conversion',
        'API Development & Integration: Connect your applications with third-party services',
        'Legacy System Modernization: Update outdated applications to modern standards',
        'Quality Assurance: Comprehensive testing including unit, integration, and user acceptance',
      ],
      process: [
        'Discovery & Requirements Gathering',
        'UX/UI Design & Prototyping',
        'Agile Development & Sprints',
        'Quality Assurance & Testing',
        'Deployment & Launch',
        'Maintenance & Support',
      ],
      perfectFor: 'Businesses needing custom software solutions, companies digitizing operations, organizations improving customer engagement.',
    },
    cctv: {
      icon: 'Video',
      title: 'CCTV & Physical Security',
      gradient: 'bg-gradient-to-r from-logo-yellow to-logo-green',
      description: 'Protect your physical assets, employees, and facilities with comprehensive security solutions. We design and implement integrated security systems that provide real-time monitoring and rapid incident response.',
      offerings: [
        'CCTV System Design: High-definition cameras, night vision, motion detection, and remote monitoring',
        'Access Control Systems: Biometric, RFID, and smart card access management',
        'Intrusion Detection: Alarm systems, sensors, and 24/7 monitoring services',
        'Video Analytics: AI-powered facial recognition, license plate recognition, and behavior analysis',
        'Integration Services: Connect physical security with your IT systems for unified management',
        'Cloud-Based Surveillance: Remote access to security feeds from anywhere, anytime',
      ],
      industries: [
        'Commercial Buildings', 'Industrial Sites', 'Educational Institutions',
        'Healthcare Facilities', 'Retail Stores', 'Warehouses',
      ],
      perfectFor: 'Businesses with physical locations, facilities requiring security compliance, organizations concerned about theft or safety.',
    },
    support: {
      icon: 'Headphones',
      title: 'IT Support & Managed Services',
      gradient: 'bg-gradient-to-r from-logo-teal to-logo-green3',
      description: 'Keep your business running smoothly with our comprehensive IT support and managed services. We provide proactive monitoring, rapid response, and expert technical support for your entire technology infrastructure.',
      offerings: [
        '24/7 Help Desk: Multi-channel support (phone, email, chat) for all your employees',
        'Network Monitoring: Proactive monitoring and alerts to prevent downtime',
        'Patch Management: Regular updates and security patches for all systems',
        'Backup & Disaster Recovery: Automated backups and rapid recovery solutions',
        'Email Management (Microsoft 365 & Outlook): User account administration, mailbox setup, configuration, and troubleshooting',
        'Microsoft Services Administration: Day-to-day management of Teams, SharePoint, OneDrive, and Azure AD',
        'Cloud Backup Management: Secure backups for emails, files, and cloud data with ongoing maintenance and data protection',
        'Hardware & Software Management: Procurement, setup, and maintenance of IT assets',
        'On-site Support: Rapid on-site assistance when remote support is not enough',
        ],
      serviceLevels: [
        { name: 'Business Hours Support', details: '8am-6pm, Next-day on-site' },
        { name: 'Extended Support', details: '24/5, 4-hour on-site' },
        { name: 'Premium Support', details: '24/7/365, 2-hour on-site' },
      ],
      perfectFor: 'Growing SMEs without internal IT teams, businesses requiring reliable 24/7 support, organizations seeking predictable IT costs.',
    },
  };

  const data = serviceData[serviceId];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    closeModal();
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  if (!data) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[1000]"
      onClick={closeModal}
    >
      <div
        className="bg-white rounded-xl max-w-2xl w-[90%] max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <div className="flex items-center">
            <div className={`w-12 h-12 ${data.gradient} rounded-lg flex items-center justify-center mr-4`}>
              {/* Icon would go here - using text for simplicity */}
              <span className="text-white text-2xl">•</span>
            </div>
            <h2 className="text-2xl font-bold text-logo-navy">{data.title}</h2>
          </div>
          <button
            onClick={closeModal}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center 
                     transition-all duration-300 hover:rotate-90"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          <div className="space-y-6">
            <p className="text-gray-700">{data.description}</p>

            <div>
              <h3 className="text-lg font-bold text-logo-navy mb-3">What We Offer:</h3>
              <ul className="list-disc pl-5 space-y-2 text-gray-600">
                {data.offerings.map((item, index) => (
                  <li key={index}>
                    <span className="font-medium text-logo-darkblue">
                      {item.split(':')[0]}:
                    </span>
                    {item.split(':')[1]}
                  </li>
                ))}
              </ul>
            </div>

            {data.benefits && (
              <div>
                <h3 className="text-lg font-bold text-logo-navy mb-3">Benefits:</h3>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  {data.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.frameworks && (
              <div>
                <h3 className="text-lg font-bold text-logo-navy mb-3">Compliance Frameworks:</h3>
                <div className="flex flex-wrap gap-2">
                  {data.frameworks.map((item, index) => (
                    <span key={index} className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-700">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data.platforms && (
              <div>
                <h3 className="text-lg font-bold text-logo-navy mb-3">Platform Expertise:</h3>
                <div className="flex flex-wrap gap-2">
                  {data.platforms.map((item, index) => (
                    <span key={index} className="bg-blue-100 text-logo-darkblue px-3 py-1 rounded-full text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data.process && (
              <div>
                <h3 className="text-lg font-bold text-logo-navy mb-3">Development Process:</h3>
                <ol className="list-decimal pl-5 space-y-1 text-gray-600">
                  {data.process.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            {data.industries && (
              <div>
                <h3 className="text-lg font-bold text-logo-navy mb-3">Industries We Serve:</h3>
                <div className="flex flex-wrap gap-2">
                  {data.industries.map((item, index) => (
                    <span key={index} className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-700">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data.serviceLevels && (
              <div>
                <h3 className="text-lg font-bold text-logo-navy mb-3">Service Levels:</h3>
                <div className="space-y-2">
                  {data.serviceLevels.map((level, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span className="font-medium">{level.name}</span>
                      <span className="text-sm text-gray-600">{level.details}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-bold text-logo-darkblue">Perfect for:</span> {data.perfectFor}
              </p>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <a
            href="#contact"
            onClick={(e) => scrollToSection(e, '#contact')}
            className="btn-primary px-6 py-2 rounded-lg"
          >
            Discuss Your Needs
          </a>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;